/**
 * 媒体文件处理工具函数
 * 用于处理拖拽、粘贴、文件选择等场景下的图片、视频、文档文件
 */

import { Platform } from 'react-native';
import { FileItem } from '../components/FilePreviewList';

/**
 * 根据文件名后缀获取 mimeType
 * 如果文件已带 mimeType，直接返回；否则根据后缀兜底
 */
export function getMimeType(fileName: string, fileType: string): string {
  if (fileType) return fileType;

  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const mimeMap: { [key: string]: string } = {
    // 文档
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    txt: 'text/plain',
    zip: 'application/zip',
    rar: 'application/x-rar-compressed',
    csv: 'text/csv',
    // 图片
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    // 视频
    mp4: 'video/mp4',
    avi: 'video/x-msvideo',
    mov: 'video/quicktime',
    wmv: 'video/x-ms-wmv',
    webm: 'video/webm',
  };
  return mimeMap[ext] || 'application/octet-stream';
}

/**
 * 获取媒体文件的宽高
 * 对于图片：获取 image 的宽高
 * 对于视频：获取 video 的宽高
 * 对于其他文件：返回 0, 0
 */
export async function getMediaDimensions(
  dataUrl: string,
  mimeType: string
): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const doc = (global as any).document || (global as any).window?.document;
    if (!doc) {
      resolve({ width: 0, height: 0 });
      return;
    }

    if (mimeType.startsWith('image/')) {
      // 处理图片
      const img = doc.createElement('img');
      img.onload = () => {
        resolve({ width: img.width || 0, height: img.height || 0 });
      };
      img.onerror = () => {
        resolve({ width: 0, height: 0 });
      };
      img.src = dataUrl;
    } else if (mimeType.startsWith('video/')) {
      // 处理视频
      const video = doc.createElement('video');
      video.preload = 'metadata';
      video.crossOrigin = 'anonymous';

      const timeout = setTimeout(() => {
        video.onloadedmetadata = null;
        video.onerror = null;
        resolve({ width: 0, height: 0 });
      }, 5000);

      video.onloadedmetadata = () => {
        clearTimeout(timeout);
        video.onloadedmetadata = null;
        video.onerror = null;
        resolve({ width: video.videoWidth || 0, height: video.videoHeight || 0 });
      };

      video.onerror = () => {
        clearTimeout(timeout);
        video.onloadedmetadata = null;
        video.onerror = null;
        resolve({ width: 0, height: 0 });
      };

      video.src = dataUrl;
    } else {
      // 其他文件类型无宽高
      resolve({ width: 0, height: 0 });
    }
  });
}

/**
 * 处理单个媒体文件，返回完整的 FileItem
 * 包括读取 DataURL 和获取宽高
 */
export async function handleMediaFile(file: File): Promise<FileItem | undefined> {
  if (Platform.OS !== 'web') {
    return undefined;
  }

  const fileName = file.name;
  const ext = fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() || '' : '';
  const mimeType = getMimeType(fileName, file.type);

  return new Promise<FileItem | undefined>((resolve) => {
    const reader = new FileReader();
    reader.onload = async (event: any) => {
      const dataUrl = event.target.result;
      const dimensions = await getMediaDimensions(dataUrl, mimeType);
      const fileItem: FileItem = {
        name: fileName,
        size: file.size,
        ext,
        url: dataUrl,
        width: dimensions.width,
        height: dimensions.height,
        mimeType,
        thumbnailPath: undefined,
        progress: undefined,
        status: undefined,
      };
      resolve(fileItem);
    };
    reader.onerror = () => resolve(undefined);
    reader.readAsDataURL(file);
  });
}

/**
 * 批量处理文件列表，返回 FileItem 数组
 * 用于处理拖拽、粘贴等场景
 */
export async function handleMultipleFiles(files: File[]): Promise<FileItem[]> {
  if (Platform.OS !== 'web') {
    return [];
  }

  const fileItems: FileItem[] = [];
  for (const file of files) {
    const result = await handleMediaFile(file);
    if (result) {
      fileItems.push(result);
    }
  }
  return fileItems;
}

/**
 * 处理粘贴事件中的剪贴板数据
 * clipboardItems 是 DataTransferItem 数组
 */
export async function handlePasteItems(items: DataTransferItem[]): Promise<FileItem[]> {
  if (Platform.OS !== 'web') {
    return [];
  }

  const fileItems: FileItem[] = [];
  for (const item of items) {
    // 只处理文件类型的剪贴板项
    if (item.type && (item.type.startsWith('image/') || item.type.startsWith('video/'))) {
      const file = item.getAsFile ? item.getAsFile() : null;
      if (file) {
        const result = await handleMediaFile(file as File);
        if (result) {
          fileItems.push(result);
        }
      }
    }
  }
  return fileItems;
}
